import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { IntegrationHealthCheck } from '../../../api/integration-health-client/integration-health-client.type';
import { IntegrationType } from '../../../api/integrations-client/integrations-client.type';
import type { StepPayload, Workflow, WorkflowPayload } from '../../../api/workflows-client/workflows-client.type';
import { iconsIntegrations } from '../../integrations/assets';

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Gets the icon URL for a given integration type
 *
 * @param {IntegrationType} source The integration type to get the icon for
 * @returns {string} The URL of the icon image for the integration type, or empty string if not found
 */
const getDataSourceIcon = (source: IntegrationType): string => {
  const sourceIcons: Record<IntegrationType, string> = {
    [IntegrationType.ADO]: iconsIntegrations.iconADO,
    [IntegrationType.JIRA]: iconsIntegrations.iconJiraCloud,
    [IntegrationType.JDC]: iconsIntegrations.iconJiraDataCenter,
    [IntegrationType.GITHUB]: iconsIntegrations.iconGithub,
    [IntegrationType.FIGMA]: iconsIntegrations.iconFigma,
    [IntegrationType.DUMMY]: iconsIntegrations.iconBloomfilter,
    [IntegrationType.BLOOMFILTER]: iconsIntegrations.iconBloomfilter,
    [IntegrationType.LINEAR]: iconsIntegrations.iconLinear,
  };
  return sourceIcons[source] || '';
};

/**
 * Gets a human-readable label for a given integration type
 *
 * @param {IntegrationType} source The integration type to get the label for
 * @returns {string} The display label for the integration type, or empty string if not found
 */
const getDataSourceLabel = (source: IntegrationType): string => {
  const sourceLabels: Record<IntegrationType, string> = {
    [IntegrationType.JIRA]: 'Jira',
    [IntegrationType.ADO]: 'ADO',
    [IntegrationType.JDC]: 'Jira Data Center',
    [IntegrationType.GITHUB]: 'Github',
    [IntegrationType.FIGMA]: 'Figma',
    [IntegrationType.DUMMY]: 'Bloomfilter',
    [IntegrationType.BLOOMFILTER]: 'Bloomfilter',
    [IntegrationType.LINEAR]: 'Linear',
  };
  return sourceLabels[source] || '';
};

/**
 * Formats a datetime string to the local timezone
 * Returns "-" for December 21, 2012 dates (placeholder indicating data was never ingested)
 * See: healthapi/apps/base_model.py - last_data_fetch default=datetime(2012, 12, 21, 23, 59)
 *
 * @param {string | undefined} datetime The datetime string to format
 * @returns {string} The formatted datetime string, "-" for never-ingested dates, or empty string if not found
 */
const formatDatetimeToLocal = (datetime: string | undefined): string => {
  if (!datetime) return '';

  const parsed = dayjs.utc(datetime);
  // December 21, 2012 is a placeholder date indicating data was never ingested
  if (parsed.year() === 2012 && parsed.month() === 11 && parsed.date() === 21) {
    return '-';
  }

  return parsed.local().format('M/D/YYYY HH:mm A z');
};

/**
 * Normalizes a time string into HH:mm format.
 * The backend expects local wall-clock times, so we simply coerce to string.
 *
 * @param {string | undefined} time The time string to format
 * @returns {string} The formatted time string, or empty string if not found
 */
const formatTimeToLocal = (time: string | undefined) => {
  if (!time) return '';
  return dayjs(`2000-01-01T${time}`).format('HH:mm');
};

/**
 * Formats a time string to the display format
 *
 * @param {string | undefined} time The time string to format
 * @returns {string} The formatted time string, or empty string if not found
 */
const formatTimeToDisplay = (time: string | undefined) => {
  if (!time) return '';
  return dayjs(`2000-01-01T${time}`).format('h:mm A');
};

/**
 * Builds a workflow payload for the API
 *
 * @param {Workflow} workflow The workflow to build the payload for
 * @returns {WorkflowPayload} The workflow payload
 */
function buildWorkflowPayload(workflow: Workflow): WorkflowPayload {
  const STEPS_TO_ORDER_INDEX = {
    Waiting: 0,
    Backlog: 1,
    'In Progress': 2,
    'In Review': 3,
    'In Test': 4,
    Deployable: 5,
    Done: 6,
  };

  const steps: StepPayload[] = workflow.steps.map((step) => {
    const stepPayload: any = {
      name: step.name,
      order_index: STEPS_TO_ORDER_INDEX[step.name as keyof typeof STEPS_TO_ORDER_INDEX],
      mappings: step.mappings.map((mapping, index) => {
        // Only include fields required by the API, omit any temporary IDs
        return {
          external_name: mapping.external_name,
          order_index: index,
          is_important: mapping.is_important,
        };
      }),
    };

    // Only include ID for steps from the backend
    if (step.id && step.id.includes('-')) {
      stepPayload.id = step.id;
    }

    return stepPayload;
  });

  return {
    name: workflow.name,
    organization: workflow.organization,
    subprojects: workflow.subprojects,
    steps,
  };
}

/**
 * Returns the display name for an integration health check issue.
 *
 * @param {IntegrationHealthCheck} issue - The integration health check issue
 * @returns {string} The display name for the integration health check issue
 */
function getDisplayName(issue: IntegrationHealthCheck): string {
  const details = issue.details as Record<string, unknown> | null | undefined;
  const name = typeof details?.name === 'string' ? details.name : undefined;
  const subprojectName = typeof details?.subproject_name === 'string' ? details.subproject_name : undefined;
  return name || subprojectName || issue.external_identifier;
}

export {
  buildWorkflowPayload,
  formatDatetimeToLocal,
  formatTimeToDisplay,
  formatTimeToLocal,
  getDataSourceIcon,
  getDataSourceLabel,
  getDisplayName,
};
